import { useMemo, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import {
  useIncidentsQuery,
  useListIncidentTypesQuery,
} from '@/graphql/generated/hooks'
import {
  IncidentOrderField,
  IncidentType,
  OrderDirection,
} from '@/graphql/generated/schemas'
import { formatDateTime } from '@/utils/formatDateTime'
import { TAILGATING_INCIDENT_SLUGS } from '@/utils/incidents'

import { IncidentOptionI } from '../../types/types'

const PAGE_SIZE = 10

export const useIncidentSearch = (
  doorId: string | undefined,
  typeFilter: IncidentType | null,
  cameraId?: string | null
) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [pageIndex, setPageIndex] = useState(0)
  const isItdEnabled = useFeatureFlag(FeatureFlag.useIncidentTypeDefinition)

  // Under ITDs the tailgating discriminator is the incidentType slug, but
  // IncidentFilterInput has no slug filter — only incidentTypeDefinitionIdIn.
  // Resolve the tailgating ITD ids so the Tailgating chip matches what
  // isTailgatingIncidentLike counts as tailgating (slug-wins). Archived ITDs
  // are included: their historical incidents still carry the id.
  const { data: incidentTypesData } = useListIncidentTypesQuery({
    skip: !isItdEnabled || typeFilter !== IncidentType.Tailgating,
  })
  const tailgatingItdIds = useMemo(
    () =>
      (incidentTypesData?.incidentTypes?.items ?? [])
        .filter(
          (item) => item.slug && TAILGATING_INCIDENT_SLUGS.includes(item.slug)
        )
        .map((item) => item.id),
    [incidentTypesData]
  )
  // Filter fields are ANDed server-side, so send exactly one type filter:
  // ITD ids when the flag is on and they have resolved (slug-wins parity with
  // isTailgatingIncidentLike), the legacy enum otherwise — including the
  // brief window while the ITD list is still loading.
  //
  // Swapping typeIn for incidentTypeDefinitionIdIn would hide any incident
  // carrying the legacy TAILGATING enum but no ITD id. Checked against
  // production incident data: in every org that has any ITD-tagged incident,
  // the count of enum-only tailgating incidents is zero — tailgating
  // incidents there always carry an ITD id, historical ones included. Orgs
  // with no tailgating ITD defined fall back to the enum via the length check
  // below, so neither population loses rows.
  const shouldUseItdFilter =
    isItdEnabled &&
    typeFilter === IncidentType.Tailgating &&
    tailgatingItdIds.length > 0

  const {
    data,
    loading: isLoading,
    error,
  } = useIncidentsQuery({
    variables: {
      filter: {
        deviceIdIn: doorId ? [doorId] : [],
        hasVideoClip: true,
        // Only offer clips the camera under test actually recorded, so a grid is
        // never tested against another camera's coordinate space (ENG-34211). The
        // field is null-tolerant by contract, so incidents whose clip predates the
        // provenance column still come back rather than the backlog disappearing.
        ...(cameraId ? { sourceCameraIdInOrNull: [cameraId] } : {}),
        ...(typeFilter
          ? shouldUseItdFilter
            ? { incidentTypeDefinitionIdIn: tailgatingItdIds }
            : { typeIn: [typeFilter] }
          : {}),
        ...(searchTerm ? { displayIdLike: searchTerm } : {}),
      },
      first: pageIndex,
      last: PAGE_SIZE,
      orderBy: [
        {
          field: IncidentOrderField.CreatedAt,
          direction: OrderDirection.Desc,
        },
      ],
    },
    skip: !doorId,
  })

  const totalCount = data?.incidents?.totalCount ?? 0
  const pageCount = Math.ceil(totalCount / PAGE_SIZE)

  const incidents: IncidentOptionI[] = useMemo(
    () =>
      (data?.incidents?.edges ?? [])
        .map((edge) => edge?.node)
        .filter(Boolean)
        .map((node) => ({
          id: node.id,
          incidentId: node.displayId,
          label: `${node.displayId} | ${formatDateTime(node.createdAt)}`,
          createdAt: node.createdAt,
          status: node.status ?? undefined,
          type: node.type ?? undefined,
          incidentTypeSlug: node.incidentType?.slug ?? undefined,
        })),
    [data]
  )

  const goToPage = (page: number) => setPageIndex(page)
  const nextPage = () => setPageIndex((p) => Math.min(p + 1, pageCount - 1))
  const previousPage = () => setPageIndex((p) => Math.max(p - 1, 0))

  // Reset to first page when filters change
  const handleSetSearchTerm = (term: string) => {
    setSearchTerm(term)
    setPageIndex(0)
  }

  return {
    incidents,
    isLoading,
    error,
    searchTerm,
    setSearchTerm: handleSetSearchTerm,
    totalCount,
    pageIndex,
    pageSize: PAGE_SIZE,
    pageCount,
    goToPage,
    nextPage,
    previousPage,
  }
}
